"use client";

/**
 * SocketBridge — renderless component that bridges auth → socket lifecycle.
 *
 * Watches the auth token from AuthContext and initializes/destroys the socket.
 * Also registers all socket event handlers (task streaming, sync, etc.)
 * and manages dashboard filter → fetch side-effects.
 *
 * Replaces the 1303-line SocketProvider wrapper.
 */

import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useConnectionStore } from "@/stores/connectionStore";
import { useBrowserStore } from "@/stores/browserStore";
import { useHistoryStore } from "@/stores/historyStore";
import { registerAllHandlers } from "@/stores/socketHandlers";
import { startTurnWatchdog } from "@/stores/turnWatchdog";
import { useDebounce } from "@/hooks/useDebounce";
import { supabase } from "@/lib/supabase/client";

export function SocketBridge(): null {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;
  const authToken = session?.access_token ?? null;
  const handlersCleanupRef = useRef<(() => void) | null>(null);

  // Initialize/destroy socket when USER IDENTITY changes (login/logout only).
  // Token refreshes are handled separately below — no need to tear down the socket.
  useEffect(() => {
    if (!authToken) {
      handlersCleanupRef.current?.();
      handlersCleanupRef.current = null;
      useConnectionStore.getState().destroySocket();
      // Clear browser state on logout — prevents stale sessions persisting
      // across profile switches.
      useBrowserStore.getState().reset();
      return;
    }

    useConnectionStore.getState().initSocket(authToken);

    // Register all task/streaming handlers on the new socket
    const socket = useConnectionStore.getState().getSocket();
    if (socket) {
      handlersCleanupRef.current = registerAllHandlers(socket);
    }

    // Connection-truth: the turn watchdog's 5s sweep lives for the socket's
    // lifetime — it probes stale-looking live turns with a cheap re-read.
    const stopWatchdog = startTurnWatchdog();

    return () => {
      stopWatchdog();
      handlersCleanupRef.current?.();
      handlersCleanupRef.current = null;
      useConnectionStore.getState().destroySocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on userId, not authToken
  }, [userId]);

  // On token refresh, kick reconnection if the socket died while the JWT was
  // stale. There is no token to push into the socket anymore: connectionStore
  // installs the FUNCTION form of `auth`, which re-reads the current Supabase
  // session on every connection attempt (mutating socket.auth here would
  // replace that function with a stale static object).
  useEffect(() => {
    if (!authToken) return;
    useConnectionStore.getState().ensureConnected();
  }, [authToken]);

  // Force a fresh Supabase session when the tab becomes visible after a long
  // idle. Without this, the socket retries forever with a stale JWT — the
  // server rejects every attempt, reconnectionAttempts:Infinity means it
  // never falls through to "give up", and the user has to hard-refresh to
  // recover. refreshSession() fires onAuthStateChange(TOKEN_REFRESHED) on
  // success, which updates session → authToken → the effect above pushes
  // the new token into socket.auth and kicks ensureConnected.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      // Only force a token refresh when the socket actually needs to reconnect.
      // If it's still connected the JWT is fine — Supabase auto-refreshes in the
      // background and the token-refresh effect above pushes the new token into
      // socket.auth — so refreshing on EVERY wake was pure churn that also
      // widened the reconnect window (a network round-trip the reconnect waited
      // on). Refresh only when disconnected: the stale-JWT-after-long-idle case
      // the connectionStore visibility handler is simultaneously reconnecting.
      const socket = useConnectionStore.getState().getSocket();
      if (socket?.connected) return;
      // Best-effort: if refresh fails (network blip, hard sign-out), let
      // AuthContext's existing SIGNED_OUT handler bounce to /login.
      supabase.auth.refreshSession().catch((err) => {
        console.warn("[SocketBridge] refreshSession on wake failed", err);
      });
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Dashboard filter → fetch side-effects
  const dashboardSearch = useHistoryStore((s) => s.dashboardSearch);
  const dashboardDateFilter = useHistoryStore((s) => s.dashboardDateFilter);
  const dashboardStatusFilter = useHistoryStore((s) => s.dashboardStatusFilter);
  const debouncedSearch = useDebounce(dashboardSearch, 300);
  const isConnected = useConnectionStore((s) => s.isSocketConnected);

  // Credits are now pushed via CREDIT_SUMMARY on socket connect — no HTTP fetch needed here

  // First connect (and any filter/search change) does the real bootstrap fetch.
  // A BARE reconnect — e.g. the WS dropped on a backgrounded tab and re-armed on
  // refocus — does a SILENT refresh instead: no historyLoading spinner, and the
  // list only re-renders if something actually changed while we were away. The
  // server auto-pushes presence and MESSAGE_SYNC reconciles the open task, so a
  // bare reconnect almost never changes the list — this kills the refetch-storm
  // churn pulse that made tab-refocus feel flaky.
  const wasEverConnectedRef = useRef(false);
  const lastFiltersRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isConnected) return;
    const filtersKey = JSON.stringify([
      debouncedSearch || "",
      dashboardDateFilter,
      dashboardStatusFilter,
    ]);
    const firstConnect = !wasEverConnectedRef.current;
    const filtersChanged =
      lastFiltersRef.current !== null && lastFiltersRef.current !== filtersKey;
    wasEverConnectedRef.current = true;
    lastFiltersRef.current = filtersKey;

    // loadTaskHistory with reset=true automatically includes stats, so no
    // separate loadTaskStats call is needed. Search + filters are AND-combined.
    useHistoryStore.getState().loadTaskHistory({
      reset: true,
      silent: !firstConnect && !filtersChanged,
      search: debouncedSearch || undefined,
      dateFilter: dashboardDateFilter,
      statusFilter: dashboardStatusFilter,
    });
  }, [
    isConnected,
    debouncedSearch,
    dashboardDateFilter,
    dashboardStatusFilter,
  ]);

  return null;
}
